import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, map } from 'rxjs';
import { PageLoaderService } from '../../../core/service/page-loader.service';
import { DryerGroup, DryerType, SelectedRow, SelectedValue, SensorEntity, SuggestedModel, TransformData } from '../../models';
import { ExactModelKeys } from '../../models/expanded-model-keys.model';
import { ProductsService } from '../../services/products.service';
import { UnitConversionService } from '../../services/unit-conversion.service';

@Component({
  selector: 'app-dryer-filter-table',
  templateUrl: './dryer-filter-table.component.html',
  styleUrls: ['./dryer-filter-table.component.scss'],
  standalone: false,
})
export class DryerFilterTableComponent implements OnInit, OnDestroy {
  private readonly MIN_INLET_TEMPERATURE = 'min_inlet_temperature';
  private readonly MIN_INLET_TEMP_LOW = 20;
  private readonly MIN_INLET_TEMP_VERY_LOW = 15;
  private readonly MAX_FLOW = 1600;
  private readonly DEFAULT_VALUE = '-';
  private readonly TAB_CONDITION = 'condition';
  private readonly TAB_MODEL = 'model';
  private subscriptions: Subscription[] = [];
  selectedRows: SelectedRow[] = [];
  maxflow: number;
  allDataItems = [];
  public displayColumns: string[] = [
    'model',
    'pressureDrop',
    'dew_point',
    'airConnection',
    'dryerType',
    'dryerCategory',
    'dryerGroup',
    'height',
    'depth',
    'width',
    'percentage',
  ];

  public columnLabels: { [key: string]: string } = {
    model: 'MODEL',
    height: 'FIELDS.HEIGHT',
    depth: 'FIELDS.DEPTH',
    width: 'FIELDS.WIDTH',
    airConnection: 'FIELDS.AIR_CONNECTIONS',
    dryerType: 'DRYER_TYPE',
    dew_point: 'NOMINAL_PRESSURE_DEW_POINT',
    pressureDrop: 'FIELDS.PRESSURE_DROP',
    percentage: 'EXTRA_AIR_FLOW_AVAILABLE',
    dryerCategory: 'DRYER_CATEGORY',
    dryerGroup: 'DRYER_GROUP',
  };

  public columnToUnitMap: { [key: string]: string } = {
    height: 'Height',
    depth: 'Depth',
    width: 'Width',
    airConnection: 'Air Connection',
    pressureDrop: 'Pressure Drop',
  };

  public displayedColumns: string[] = ['select', ...this.displayColumns, 'download'];
  imageSource: string | null = null;
  @Input() tab: string = '';
  dataSource = new MatTableDataSource<SelectedRow>([]);
  filteredSelectedRows: SelectedRow[] = [];
  modelAvailable: boolean = false;
  modelName: string = '';
  @Input() sensorValues: TransformData;
  @Input() activeTab: string = '';
  @Output() modelEmitter = new EventEmitter<string>();
  public loadingContent = false;
  selectedValues: SelectedValue[];
  fetchSensorValues: any;
  powerSupply!: string;
  showNoModelsMessage: boolean = false;
  objectKeys = Object.keys;
  dryerTypeLabel: string = '';
  selectedUnit: string = 'm³/h';
  units = {
    'Pressure Drop': 'bar',
    'Dew Point': '°C',
    'Net Weight': 'kg',
    Height: 'mm',
    Depth: 'mm',
    Width: 'mm',
    'Air Connection': 'Rp',
    'Nominal Consumption': 'kW',
    'Nominal Current': 'A',
    'Full Load Current': 'A',
    'Locked Rotor Current': 'A',
    'Nominal Flow': 'm³/h',
  };
  private readonly MAX_SELECTED_ROWS = 4;
  private pendingSuggestedModelRequests = 0;
  private dataGeneration = 0;

  private static readonly DRYER_GROUPS = {
    REFRIGERATED: 'refrigerated',
    DESICCANT: 'desiccant',
    SUBFREEZING: 'subfreezing',
    SUB_FREEZING: 'sub-freezing',
    VSD: 'vsd',
  } as const;

  private static readonly DRYER_CATEGORIES = {
    CYCLING: 'cycling',
    NON_CYCLING: 'non',
    HIGH_TEMP: 'high',
    TEMPERATURE: 'temperature',
    TEMP: 'temp',
    MODULAR: 'modular',
    TWIN: 'twin',
    TOWER: 'tower',
  } as const;

  private static readonly DRYER_TYPES = { AIR: 'air', WATER: 'water', HEATED: 'heated', HEAT: 'heat', HEATLESS: 'heatless' } as const;

  private static readonly HIGH_TEMP_KEYWORDS = ['high', 'temperature', 'temp', 'ht'] as const;

  private static readonly IBA_SUFFIX = 'IBA';
  private static readonly IBA_AIRFLOW_THRESHOLD = 15;
  private static readonly DEFAULT_AIRFLOW_THRESHOLD = 10;

  private readonly DRYER_ACCORDION_CONFIG = {
    refrigeratedCycling: {
      id: 'refrigeratedCycling',
      titleKey: 'ACCORDIAN_DRYER_TYPES.REFRIGERATED_AIR_COOLED_CYCLING',
      order: 1,
      specifications: {
        airflow: '1 to 24188 m³/h',
        pressure: '3 to 16 barg',
        inletTemp: '10 to 60 °C',
        ambientTemp: '5 to 46 °C',
        dewPoint: '3 to 10 °C',
      },
      matchCriteria: { groups: ['refrigerated'], categories: ['cycling'], types: ['air'], excludeCategories: ['non-cycling', 'non'] },
    },
    refrigeratedNonCycling: {
      id: 'refrigeratedNonCycling',
      titleKey: 'ACCORDIAN_DRYER_TYPES.REFRIGERATED_AIR_COOLED_NON_CYCLING',
      order: 2,
      specifications: {
        airflow: '1 to 24188 m³/h',
        pressure: '3 to 16 barg',
        inletTemp: '10 to 60 °C',
        ambientTemp: '5 to 50 °C',
        dewPoint: '3 to 10 °C',
      },
      matchCriteria: { groups: ['refrigerated'], categories: ['non-cycling', 'non'], types: ['air'] },
    },
    refrigeratedWaterCooled: {
      id: 'refrigeratedWaterCooled',
      titleKey: 'ACCORDIAN_DRYER_TYPES.REFRIGERATED_WATER_COOLED_NON_CYCLING',
      order: 3,
      specifications: {
        airflow: '1 to 24188 m³/h',
        pressure: '3 to 16 barg',
        inletTemp: '10 to 60 °C',
        ambientTemp: '5 to 50 °C',
        dewPoint: '3 to 10 °C',
      },
      matchCriteria: { groups: ['refrigerated'], types: ['water'], categories: ['cooled'] },
    },
    refrigeratedHighTemp: {
      id: 'refrigeratedHighTemp',
      titleKey: 'ACCORDIAN_DRYER_TYPES.REFRIGERATED_AIR_COOLED_HIGH_TEMPERATURE',
      order: 4,
      specifications: {
        airflow: '1 to 24188 m³/h',
        pressure: '3 to 16 barg',
        inletTemp: '32 to 93 °C',
        ambientTemp: '5 to 50 °C',
        dewPoint: '3 to 10 °C',
      },
      matchCriteria: { groups: ['refrigerated'], categories: ['high', 'temperature', 'temp', 'ht'], types: ['air'] },
    },
    subfreezingAirCooled: {
      id: 'subfreezingAirCooled',
      titleKey: 'ACCORDIAN_DRYER_TYPES.SUBFREEZING_AIR_COOLED',
      order: 5,
      specifications: { airflow: '100 to 2400 m³/h', pressure: '2.5 to 14 barg', inletTemp: '20 to 50 °C', ambientTemp: '5 to 45 °C' },
      matchCriteria: { groups: ['subfreezing', 'sub-freezing', 'sub_freezing'], types: ['air'] },
    },
    desiccantHeated: {
      id: 'desiccantHeated',
      titleKey: 'ACCORDIAN_DRYER_TYPES.DESICCANT_HEATED',
      order: 6,
      specifications: { airflow: '150 to 27035 m³/h', pressure: '5 to 10 barg', inletTemp: '15 to 50 °C', ambientTemp: '10 to 46 °C' },
      matchCriteria: { groups: ['desiccant'], types: ['heated', 'heat', 'externally'] },
    },
    desiccantTwinTower: {
      id: 'desiccantTwinTower',
      titleKey: 'ACCORDIAN_DRYER_TYPES.DESICCANT_HEATLESS_TWIN_TOWER',
      order: 7,
      specifications: { airflow: '50 to 27035 m³/h', pressure: '5 to 10 barg', inletTemp: '15 to 50 °C', ambientTemp: '10 to 50 °C' },
      matchCriteria: { groups: ['desiccant'], categories: ['twin', 'tower'], types: ['heatless'] },
    },
    desiccantModular: {
      id: 'desiccantModular',
      titleKey: 'ACCORDIAN_DRYER_TYPES.DESICCANT_HEATLESS_MODULAR',
      order: 8,
      specifications: { airflow: '1.5 to 27035 m³/h', pressure: '4 to 14 barg', inletTemp: '15 to 50 °C', ambientTemp: '10 to 46 °C' },
      matchCriteria: { groups: ['desiccant'], categories: ['modular'], types: ['heatless'] },
    },
    vsdAirCooled: {
      id: 'vsdAirCooled',
      titleKey: 'ACCORDIAN_DRYER_TYPES.VSD_AIR_COOLED',
      order: 9,
      specifications: { pressure: '2 to 16 barg', inletTemp: '20 to 70 °C', ambientTemp: '1 to 45 °C' },
      matchCriteria: { groups: ['vsd'], types: ['air'] },
    },
    vsdWaterCooled: {
      id: 'vsdWaterCooled',
      titleKey: 'ACCORDIAN_DRYER_TYPES.VSD_WATER_COOLED',
      order: 10,
      specifications: { pressure: '2 to 16 barg', inletTemp: '20 to 70 °C', ambientTemp: '1 to 45 °C', waterInletTemp: '15 to 30 °C' },
      matchCriteria: { groups: ['vsd'], types: ['water'] },
    },
  } as const;

  // Specification labels mapping
  private readonly SPEC_LABELS = {
    airflow: 'ACCORDIAN_DRYER_TYPES.SPECIFICATIONS.AIRFLOW',
    pressure: 'ACCORDIAN_DRYER_TYPES.SPECIFICATIONS.PRESSURE_RANGE',
    inletTemp: 'ACCORDIAN_DRYER_TYPES.SPECIFICATIONS.INLET_TEMPERATURE_RANGE',
    ambientTemp: 'ACCORDIAN_DRYER_TYPES.SPECIFICATIONS.AMBIENT_TEMPERATURE_RANGE',
    dewPoint: 'ACCORDIAN_DRYER_TYPES.SPECIFICATIONS.DEW_POINT_RANGE',
    waterInletTemp: 'ACCORDIAN_DRYER_TYPES.SPECIFICATIONS.WATER_INLET_TEMPERATURE_RANGE',
  } as const;

  expandedAccordions: Record<string, boolean> = {};

  constructor(
    private productservice: ProductsService,
    private unitConversionService: UnitConversionService,
    private pageLoaderService: PageLoaderService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    const loaderSub = this.pageLoaderService.onPageEvent((loading) => {
      Promise.resolve().then(() => {
        this.loadingContent = loading;
        this.cdr.detectChanges();
      });
    });
    this.subscriptions.push(loaderSub);
  }

  ngOnInit(): void {
    this.resetAllAccordions();

    this.subscriptions.push(
      this.productservice.sensorValues$.subscribe((data) => {
        this.fetchSensorValues = data ? JSON.parse(JSON.stringify(data)) : null;
      }),
    );

    this.subscriptions.push(
      this.unitConversionService?.selectedMaxFlowUnit$.subscribe((unit) => {
        this.selectedUnit = unit;
      }),
    );
    this.subscriptions.push(
      this.productservice.selectedValues$.subscribe((value) => {
        this.selectedValues = value ?? [];
        this.updateAccordionExpansion();
        this.showNoModelsMessage = false;
      }),
    );

    if (this.tab === this.TAB_CONDITION) {
      this.setupConditionTab();
    } else if (this.tab === this.TAB_MODEL) {
      this.setupModelTab();
    }
  }

  get visibleAccordions() {
    return Object.values(this.DRYER_ACCORDION_CONFIG)
      .filter((config) => this.shouldShowAccordion(config.id))
      .sort((a, b) => a.order - b.order);
  }
  getTranslatedFieldLabel(fieldKey: string): string {
    const fieldTranslationMap: { [key: string]: string } = {
      'Nominal Flow': 'NOMINAL_FLOW_RATE',
      'Pressure Drop': 'FIELDS.PRESSURE_DROP',
      'Net Weight': 'FIELDS.NET_WEIGHT',
      Height: 'FIELDS.HEIGHT',
      Width: 'FIELDS.WIDTH',
      Depth: 'FIELDS.DEPTH',
      'Air Connection': 'FIELDS.AIR_CONNECTIONS',
      'Cooling Media': 'FIELDS.COOLING_MEDIA',
      'Nominal Consumption': 'FIELDS.NOMINAL_CONSUMPTION',
      'Nominal Current': 'FIELDS.NOMINAL_CURRENT',
      'Full Load Current': 'FIELDS.FULL_LOAD_CURRENT',
      'Locked Rotor Current': 'FIELDS.LOCKED_ROTOR_CURRENT',
      'Power Supply': 'FIELDS.POWER_SUPPLY',
      'Dryer Group': 'DRYER_GROUP',
    };

    return fieldTranslationMap[fieldKey] || fieldKey;
  }

  trackByAccordionId(index: number, config: any): string {
    return config.id;
  }

  trackBySpecKey(index: number, spec: any): string {
    return spec.key;
  }

  shouldShowAccordion(accordionId: string): boolean {
    const config = this.DRYER_ACCORDION_CONFIG[accordionId as keyof typeof this.DRYER_ACCORDION_CONFIG];
    if (!config) {
      return false;
    }
    if (this.expandedAccordions[accordionId]) {
      return true;
    }
    if (this.showNoModelsMessage) {
      return this.matchesSelectionCriteria(config) || this.matchesOutOfRangeItems(config);
    }

    return false;
  }

  private matchesSelectionCriteria(config: any): boolean {
    if (!this.selectedValues?.length) {
      return true;
    }

    return this.selectedValues.some((value) => {
      // Extract the main dryer information
      const grandparent = (value.grandparent?.toLowerCase() || '').trim();
      const label = (value.label?.toLowerCase() || '').trim();

      // Create search text from grandparent and label
      const mainSearchText = `${grandparent} ${label}`.trim();

      // Check if main criteria matches
      const mainMatches = this.matchesCriteria(mainSearchText, config.matchCriteria);

      // Also check children categories if they exist
      const childrenMatch =
        value.children?.some((child) => {
          const childCategory = (child.dryercategory?.toLowerCase() || '').trim();
          const childSearchText = `${grandparent} ${label} ${childCategory}`.trim();
          return this.matchesCriteria(childSearchText, config.matchCriteria);
        }) ?? false;

      return mainMatches || childrenMatch;
    });
  }

  getTranslationKey(element: SelectedRow, column: string): string {
    const value = this.getColumnValue(element, column);

    if (value === '-' || !value || value === null) {
      return '-';
    }

    if (column === 'dryerType') {
      const stringValue = String(value);

      switch (stringValue) {
        case 'Air Cooled':
          return 'AIR_COOLED';
        case 'Water Cooled':
          return 'WATER_COOLED';
        case 'Heated':
          return 'HEATED';
        case 'Heatless':
          return 'HEATLESS';
        default:
          return 'UNKNOWN_DRYER_TYPE';
      }
    }

    if (column === 'dryerCategory') {
      const stringValue = String(value);

      switch (stringValue) {
        case 'Non-Cycling Refrigerated':
          return 'NON_CYCLING_REFRIGERATED';
        case 'High Temperature Refrigerated':
          return 'HIGH_TEMPERATURE_REFRIGERATED';
        case 'Cycling Refrigerated':
          return 'CYCLING_REFRIGERATED';
        case 'Twin Tower':
          return 'TWIN_TOWER';
        case 'Desiccant Modular':
          return 'DESICCANT_MODULAR';
        case 'Standard':
          return 'STANDARD';
        case 'VSD':
          return 'VSD';
        default:
          return 'UNKNOWN_DRYER_CATEGORY';
      }
    }

    if (column === 'dryerGroup') {
      const stringValue = String(value);

      switch (stringValue) {
        case 'Subfreezing Dryers':
          return 'SUBFREEZING_DRYERS';
        case 'Refrigerated Dryers':
          return 'REFRIGERATED_DRYERS';
        case 'Desiccant Dryers':
          return 'DESICCANT_DRYERS';
        case 'VSD Dryers':
          return 'VSD_DRYERS';
        default:
          return 'UNKNOWN_DRYER_GROUP';
      }
    }

    return 'UNKNOWN_VALUE';
  }
  private matchesOutOfRangeItems(config: any): boolean {
    return this.allDataItems.some((item) => {
      if (item.withinRange && item.exactModel) {
        return false;
      }
      const itemData = {
        group: (item.dryerGroup?.toLowerCase() || '').trim(),
        category: (item.dryerCategory?.toLowerCase() || '').trim(),
        type: (item.dryerType?.toLowerCase() || '').trim(),
      };

      const searchText = `${itemData.group} ${itemData.category} ${itemData.type}`.trim();
      return this.matchesCriteria(searchText, config.matchCriteria);
    });
  }

  private matchesCriteria(text: string, criteria: any): boolean {
    const hasRequiredGroup = criteria.groups?.some((group: string) => text.includes(group.toLowerCase())) ?? false;

    if (!hasRequiredGroup) {
      return false;
    }
    const hasCategoryMatch = !criteria.categories?.length || criteria.categories.some((cat: string) => text.includes(cat.toLowerCase()));
    const hasTypeMatch = !criteria.types?.length || criteria.types.some((type: string) => text.includes(type.toLowerCase()));
    const hasExclusion = criteria.excludeCategories?.some((cat: string) => text.includes(cat.toLowerCase())) ?? false;

    return hasCategoryMatch && hasTypeMatch && !hasExclusion;
  }

  private updateAccordionExpansion(): void {
    this.resetAllAccordions();

    Object.entries(this.DRYER_ACCORDION_CONFIG).forEach(([key, config]) => {
      const shouldExpand = this.shouldExpandAccordion(config);
      this.expandedAccordions[key] = shouldExpand;
    });
  }

  private shouldExpandAccordion(config: any): boolean {
    if (this.matchesSelectionCriteria(config)) {
      return true;
    }

    if (this.showNoModelsMessage && this.matchesOutOfRangeItems(config)) {
      return true;
    }

    return false;
  }

  getSpecificationEntries(specifications: Record<string, string>): Array<{ key: string; label: string; value: string }> {
    return Object.entries(specifications).map(([key, value]) => ({
      key,
      label: this.SPEC_LABELS[key as keyof typeof this.SPEC_LABELS] || key,
      value,
    }));
  }

  isAccordionExpanded(accordionId: string): boolean {
    return this.expandedAccordions[accordionId] || false;
  }

  toggleAccordion(accordionId: string): void {
    this.expandedAccordions[accordionId] = !this.expandedAccordions[accordionId];
  }

  private setupConditionTab(): void {
    const cachedResults = this.productservice.getProcessedConditionResults();
    let skipInitialEmit = cachedResults.length > 0;

    if (skipInitialEmit) {
      // Restore table rows but reset all user-interaction state so stale selection
      // and suggested-model data from the previous session do not persist.
      this.dataSource.data = cachedResults.map((row) => ({ ...row, selected: false }));
      this.selectedRows = [];
      this.filteredSelectedRows = [];
      this.modelName = '';
    }

    this.subscriptions.push(
      this.productservice.fetchedData$.subscribe((fetchedData) => {
        if (skipInitialEmit) {
          skipInitialEmit = false;
          return;
        }
        // Increment generation so any in-flight suggested-model requests from a
        // previous fetch are recognised as stale and discarded when they resolve.
        this.dataGeneration++;
        this.selectedRows = [];
        this.filteredSelectedRows = [];
        this.productservice.clearSelectedCheckboxes();
        this.productservice.setFilteredSelectedRows([]);
        this.dataSource.data = fetchedData ? this.processData(JSON.parse(JSON.stringify(fetchedData))) : [];
        this.processConditionData();
      }),
    );
  }

  private processConditionData(): void {
    this.pendingSuggestedModelRequests = 0;
    this.dataSource?.data.forEach((item: SelectedRow) => {
      item.warningMessage = null;
      item.isHeaterRequired = false;
      if (item?.exactModel) {
        this.modelName = item.exactModel?.model;
        this.modelEmitter.emit(this.modelName);
        this.checkHeaterRequirement(item);
        this.fetchSuggestedModels(item);
      }
    });
  }

  private checkHeaterRequirement(item: SelectedRow): void {
    if (item.exactModel?.dryerGroup !== DryerGroup?.SUBFREEZING_DRYERS) {
      return;
    }

    const sensorValues = this.fetchSensorValues?.sensorValues;
    const minInlet = sensorValues?.min_inlet_temperature;
    if (!sensorValues || !(this.MIN_INLET_TEMPERATURE in sensorValues) || minInlet === undefined) {
      return;
    }

    if (minInlet < this.MIN_INLET_TEMP_LOW && item?.exactModel?.maxFlow !== this.MAX_FLOW) {
      this.handleLowInletTempHeater(item);
    } else if (minInlet < this.MIN_INLET_TEMP_VERY_LOW && item?.exactModel?.maxFlow === this.MAX_FLOW) {
      item.warningMessage = 'Minimum air inlet temperature is too low, at least +15°C required in SubFreezing Dryers.';
    } else if (minInlet < this.MIN_INLET_TEMP_LOW && item?.exactModel?.maxFlow === this.MAX_FLOW) {
      item.warningMessage = 'Minimum air inlet temperature is too low, pressure dew point not guaranteed in SubFreezing Dryers';
    } else {
      item.warningMessage = '';
    }
  }

  private handleLowInletTempHeater(item: SelectedRow): void {
    if (!item.exactModel.model.includes('Low inlet temp heater')) {
      item.isHeaterRequired = true;
      item.exactModel.model += '+ Low inlet temp heater';
      item.warningMessage =
        'Compressed air with a low inlet temperature heater is required for temperatures below 20°C in Sub-Freezing Dryers.';
    }
  }

  private fetchSuggestedModels(item: SelectedRow): void {
    const generation = this.dataGeneration;
    const persistedSensorValues = this.fetchSensorValues?.sensorValues;
    const payload = {
      data: [JSON.parse(JSON.stringify(item?.exactModel))],
      pressure: this.sensorValues?.pressure ?? persistedSensorValues?.pressure,
      airFlow: this.sensorValues?.air_flow ?? persistedSensorValues?.air_flow,
      calculatedId: item?.calculatedId,
      airFlowMargin: item?.percentage,
      sensorValues: persistedSensorValues,
    };

    this.pendingSuggestedModelRequests++;
    this.subscriptions.push(
      this.productservice.getSuggestedModels(payload).subscribe(
        (models) => {
          this.pendingSuggestedModelRequests--;
          if (generation !== this.dataGeneration) {
            return;
          }
          item.suggestedModels = JSON.parse(JSON.stringify(models));
          this.dataSource.data = this.processSuggestedModels(JSON.parse(JSON.stringify(this.dataSource.data)), models, item?.calculatedId);
          if (this.pendingSuggestedModelRequests === 0) {
            this.productservice.updateProcessedConditionResults([...this.dataSource.data]);
          }
        },
        (_error) => {
          this.pendingSuggestedModelRequests--;
          if (this.pendingSuggestedModelRequests === 0) {
            this.productservice.updateProcessedConditionResults([...this.dataSource.data]);
          }
        },
      ),
    );
  }

  private setupModelTab(): void {
    this.subscriptions.push(
      this.productservice.modelData$.subscribe((modelData) => {
        this.selectedRows = [];
        this.filteredSelectedRows = [];
        this.productservice.clearSelectedCheckboxes();
        this.productservice.setFilteredSelectedRows([]);
        this.dataSource.data = modelData ? this.processData(JSON.parse(JSON.stringify(modelData))) : [];
        this.processModelData();
      }),
    );
  }

  private processModelData(): void {
    this.dataSource?.data.forEach((item: SelectedRow) => {
      item.isHeaterRequired = false;
      if (item?.exactModel) {
        this.modelName = item?.exactModel?.model;
        this.modelEmitter.emit(this.modelName);
        this.checkHeaterRequirement(item);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  trackByRow(_index: number, row: SelectedRow): any {
    return row.calculatedId ?? (row as any).model ?? _index;
  }

  getDetailUnit(key: string): string {
    // Nominal flow is converted to the selected flow unit, so its label must follow that unit
    if (key === ExactModelKeys.maxFlow) {
      return ' ' + this.selectedUnit;
    }
    const unit = (this.units as Record<string, string>)[key];
    return unit ? ' ' + unit : '';
  }

  getFilteredEntries(row): { key: string; value }[] {
    const excludedKeys = [
      'id',
      'sensors',
      'expanded',
      'percentage',
      'pressureDrop',
      'convertedMaxFlow',
      'isHeaterRequired',
      'model',
      'airConnection',
      'height',
      'depth',
      'width',
      'dryerType',
      'dryerCategory',
      'imageSource',
      'isImageLoading',
    ];

    const model = row.exactModel || row;
    const entries = this.getFilteredEntriesArray(model, excludedKeys);
    return this.sortEntries(entries);
  }

  private computeRowSideEffects(row: any): void {
    const model = row.exactModel || row;
    this.maxflow = model.maxFlow;

    const phase = this.getSensorValue(row, 'phase');
    const voltage = this.getSensorValue(row, 'voltage');
    const frequency = this.getSensorValue(row, 'frequency');

    if (phase && voltage && frequency) {
      this.powerSupply = `${voltage}/${phase}/${frequency}`;
    }

    this.checkLowInletTemp(row, model);
  }

  public isPercentageNegative(row: SelectedRow): boolean {
    if (this.tab !== this.TAB_MODEL) {
      return false;
    }
    if (row.percentage !== null && row.percentage !== undefined && !Number.isNaN(Number(row.percentage))) {
      return Number(row.percentage) < 0;
    }
    return false;
  }

  private getSensorValue(row, sensorName: string) {
    return (
      row.exactModel?.sensors?.find((d: { name: string }) => d.name === sensorName)?.value ||
      row.sensors?.find((d: { name: string }) => d.name === sensorName)?.value
    );
  }

  private checkLowInletTemp(row, model) {
    if (
      this.fetchSensorValues?.sensorValues &&
      this.MIN_INLET_TEMPERATURE in this.fetchSensorValues.sensorValues &&
      (row?.exactModel?.dryerGroup == 'Subfreezing Dryers' || row?.dryerGroup == 'Subfreezing Dryers')
    ) {
      const minInlet = this.fetchSensorValues.sensorValues.min_inlet_temperature;
      if (minInlet < 20 && this.maxflow !== 1600) {
        if (!model.model.includes('Low inlet temp heater')) {
          model.model += '+ Low inlet temp heater ';
        }
      }
    }
  }

  private getFilteredEntriesArray(model, excludedKeys) {
    return Object.entries(model)
      .filter(([key, value]) => !excludedKeys.includes(key))
      .map(([key, value]) => ({
        key: ExactModelKeys[key as keyof typeof ExactModelKeys] || key,
        value: value != null || value != undefined ? value : '-',
      }));
  }

  private sortEntries(entries) {
    const filteredEntries = entries.filter((entry) => entry.value !== '-');
    const indexMap = new Map<string, number>(entries.map((e, i) => [e.key, i] as [string, number]));

    return filteredEntries.sort((a, b) => (indexMap.get(a.key) ?? 0) - (indexMap.get(b.key) ?? 0));
  }

  downloadPdf(event: Event, modelname: string, isHeaterRequired: boolean): void {
    event.stopPropagation();
    const sanitizedModelName = modelname.split('+')[0].trim();
    const sensorValuesWithPressureDrop = this.fetchSensorValues ? JSON.parse(JSON.stringify(this.fetchSensorValues)) : { sensorValues: {} };
    const selectedRow = this.dataSource.data.find(
      (row) => (row.exactModel?.model || row.model || '').split('+')[0].trim() === sanitizedModelName,
    );

    if (selectedRow) {
      const pressureDrop = this.getPressureDropValue(selectedRow);
      let numericPressureDrop = pressureDrop;
      if (typeof pressureDrop === 'string' && pressureDrop !== this.DEFAULT_VALUE) {
        numericPressureDrop = Number.parseFloat(pressureDrop);
        if (Number.isNaN(numericPressureDrop)) {
          numericPressureDrop = pressureDrop;
        }
      }

      if (sensorValuesWithPressureDrop.sensorValues) {
        sensorValuesWithPressureDrop.sensorValues.pressureDrop = numericPressureDrop;
      }
    }
    this.productservice.getDownloadPdf(sanitizedModelName, isHeaterRequired, sensorValuesWithPressureDrop);
  }

  pushRowWithoutModelString(row: SelectedRow): void {
    // Create a deep clone of the row to avoid modifying the original display data
    const clonedRow = JSON.parse(JSON.stringify(row));

    // Store the original model name with heater information for UI display
    const originalModelName = clonedRow.exactModel?.model || clonedRow.model || '';

    // Keep track of whether a heater is required
    const hasHeater = clonedRow.isHeaterRequired || (originalModelName && originalModelName.includes('Low inlet temp heater'));

    // Preserve the heater requirement flag explicitly for the UI
    clonedRow.isHeaterRequired = hasHeater;

    // Clean the model names for backend processing
    this.cleanModelNames(clonedRow);

    const rowIdentifier = this.getUniqueIdentifier(row);
    const index = this.filteredSelectedRows.findIndex((r) => this.getUniqueIdentifier(r) === rowIdentifier);

    if (index === -1) {
      this.filteredSelectedRows.push(clonedRow);
    } else {
      this.filteredSelectedRows[index] = clonedRow;
    }

    // Update the service with the complete list
    this.productservice.setFilteredSelectedRows(this.filteredSelectedRows);
  }

  private cleanModelNames(row: SelectedRow): void {
    // Clean the model name for backend processing
    if (row.exactModel?.model && row.exactModel.model.includes('+')) {
      row.exactModel.model = row.exactModel.model.split('+')[0].trim();
    }

    if (row.model && row.model.includes('+')) {
      row.model = row.model.split('+')[0].trim();
    }
  }

  private getUniqueIdentifier(item: SelectedRow): string {
    if (item.exactModel?.['id']) {
      return `exact-${item.exactModel?.['id']}`;
    }
    if (item?.['id']) {
      return `row-${item?.['id']}`;
    }
    const modelName = item.exactModel?.model || item.model || '';
    const maxFlow = item.exactModel?.maxFlow || item.maxFlow || '';
    const dryerType = item.exactModel?.dryerType || item?.['dryerType'] || '';

    return `${modelName}-${maxFlow}-${dryerType}`;
  }

  getColumnValue(element: SelectedRow, column: string): number | string {
    if (column === 'dew_point') {
      return this.getDewPointValue(element);
    }
    if (column === 'pressureDrop') {
      return this.getPressureDropValue(element);
    }
    if (column === 'percentage') {
      if (this.tab === this.TAB_MODEL) {
        return element.percentage !== undefined ? `${element.percentage}%` : '0%';
      } else {
        return element.percentage !== undefined && element.percentage > 0 ? `${element.percentage}%` : '0%';
      }
    }

    if (element.exactModel && element.exactModel[column] !== undefined && typeof element.exactModel === 'object') {
      return element.exactModel[column] || this.DEFAULT_VALUE;
    }
    return element[column] || this.DEFAULT_VALUE;
  }

  private getDewPointValue(element: SelectedRow): number | string {
    if (element?.exactModel) {
      if (element?.exactModel?.dryerGroup === DryerGroup.DESICCANT_DRYERS && element?.exactModel?.dryerType === DryerType.HEATLESS) {
        return this.fetchSensorValues?.sensorValues?.desiccant_dew_point || this.DEFAULT_VALUE;
      } else {
        return this.getDewPoint(element.exactModel?.sensors) || this.DEFAULT_VALUE;
      }
    } else if (element) {
      if (element?.['dryerGroup'] === DryerGroup.DESICCANT_DRYERS && element?.['dryerType'] === DryerType.HEATLESS) {
        return this.fetchSensorValues?.sensorValues?.desiccant_dew_point || this.DEFAULT_VALUE;
      }
      return this.getDewPoint(element?.['sensors']) || this.DEFAULT_VALUE;
    }
    return this.DEFAULT_VALUE;
  }

  private markRecommendedRows(rows: SelectedRow[]): void {
    rows.forEach((row) => {
      row.isRecommended = false;
      row.isBelowThreshold = false;
    });

    // The backend returns one exact model per selected dryer category (each with its own
    // suggested models). Recommend one green row PER category: within each group, the first
    // model whose extra-air-flow % meets the category's threshold is green; below-threshold
    // models are greyed out. Greying only applies on the condition tab.
    const applyGrey = this.tab !== this.TAB_MODEL;

    this.groupRowsByCategory(rows).forEach((groupRows) => {
      const threshold = this.getGroupThreshold(groupRows);
      const sortedRows = [...groupRows].sort((a, b) => (a.percentage ?? 0) - (b.percentage ?? 0));
      let greenAssigned = false;

      for (const row of sortedRows) {
        const percentage = row.percentage ?? 0;
        if (percentage < threshold) {
          if (applyGrey) {
            row.isBelowThreshold = true;
          }
        } else if (!greenAssigned) {
          row.isRecommended = true;
          greenAssigned = true;
        }
      }
    });
  }

  private groupRowsByCategory(rows: SelectedRow[]): Map<string, SelectedRow[]> {
    const groups = new Map<string, SelectedRow[]>();
    for (const row of rows) {
      const key = this.getCategoryGroupKey(row);
      const group = groups.get(key);
      if (group) {
        group.push(row);
      } else {
        groups.set(key, [row]);
      }
    }
    return groups;
  }

  private getCategoryGroupKey(row: any): string {
    // Group by the category signature so every distinct dryer category visible in the table
    // gets its own recommended (green) row. A category's exact model and its suggested models
    // share the same group/type/category, so they fall into the same group.
    const source = row.exactModel ?? row;
    const signature = `${source?.dryerGroup ?? ''}|${source?.dryerType ?? ''}|${source?.dryerCategory ?? ''}`;
    if (signature !== '||') {
      return `sig-${signature}`;
    }
    // Fallback when category fields are missing: use the backend id grouping. Suggested-model
    // rows are tagged with their parent exact model's id; exact-model rows carry calculatedId.
    const groupId = row.categoryGroupId ?? row.calculatedId;
    return groupId !== undefined && groupId !== null ? `id-${groupId}` : 'sig-unknown';
  }

  private getGroupThreshold(groupRows: SelectedRow[]): number {
    const exactRow = groupRows.find((r) => !!r.exactModel);
    const modelName = exactRow?.exactModel?.model || groupRows.find((r) => !!r.model)?.model || '';
    const isIba = modelName.toUpperCase().endsWith(DryerFilterTableComponent.IBA_SUFFIX);
    return isIba ? DryerFilterTableComponent.IBA_AIRFLOW_THRESHOLD : DryerFilterTableComponent.DEFAULT_AIRFLOW_THRESHOLD;
  }

  getRowClasses(row: SelectedRow): { [key: string]: boolean } {
    const shouldBeGray = this.tab === this.TAB_MODEL && this.isPercentageNegative(row);

    if (shouldBeGray) {
      return {
        'bg-danger-subtle': true,
        'text-danger-emphasis': true,
      };
    }

    if (row.isBelowThreshold) {
      // Dedicated class (not Bootstrap's bg-secondary-subtle): this theme overrides
      // --bs-secondary-bg-subtle to white, so the utility class would render white, not grey.
      return { 'below-threshold-row': true };
    }

    return { 'exact-model': !!row.isRecommended, 'bg-info': !row.isRecommended, 'bg-data-table-row': !!row.isRecommended };
  }

  private getPressureDropValue(element: SelectedRow): number | string {
    if (element?.pressureDrop !== undefined) {
      const pressureDrop = Number.parseFloat(element.pressureDrop);
      if (!Number.isNaN(pressureDrop)) {
        if (this.fetchSensorValues?.sensorValues) {
          this.fetchSensorValues.sensorValues.pressureDrop = pressureDrop.toFixed(2);
        }
        return pressureDrop.toFixed(2);
      }
    }
    // Non-numeric or missing pressure drop (e.g. NaN) — show the placeholder, never "NaN".
    return this.DEFAULT_VALUE;
  }

  // Guards the template's `| number` pipe: it throws on NaN/non-numeric input. Returns true
  // only when pressureDrop is a finite number, so the template can fall back to '-' otherwise.
  hasValidPressureDrop(row: SelectedRow): boolean {
    return row?.pressureDrop !== null && row?.pressureDrop !== undefined && !Number.isNaN(Number.parseFloat(row.pressureDrop));
  }

  private processSuggestedModels(existingData: any[], suggestedModelsData: any, parentCategoryGroupId?: number): any[] {
    const processedData: any[] = [...existingData];

    if (suggestedModelsData?.closestModel) {
      suggestedModelsData.closestModel.forEach((modelData: SuggestedModel) => {
        // Initialize heater requirement flag
        modelData.isHeaterRequired = false;

        // Check if this is a Subfreezing Dryer
        if (modelData.model.dryerGroup === 'Subfreezing Dryers') {
          this.handleSubfreezingDryerWarnings(modelData);
        }

        // Create model with percentage data
        const modelWithPercentage = {
          ...modelData.model,
          percentage: modelData.matchPercentage,
          pressureDrop: modelData.pressureDrop,
          warningMessage: modelData.warningMessage,
          isHeaterRequired: modelData.isHeaterRequired,
          // Tag with the parent exact model's id so the suggestion stays in its category group.
          categoryGroupId: parentCategoryGroupId,
        };

        // Only add if model doesn't already exist in the data.
        // Exact-model rows have the name at data.exactModel.model (not data.model), so check both.
        const modelExists = processedData.some(
          (data) => data.model === modelWithPercentage.model || data.exactModel?.model === modelWithPercentage.model,
        );
        if (!modelExists) {
          processedData.push(modelWithPercentage);
        }
      });
    }

    // Sort by percentage (lowest first)
    processedData.sort((a, b) => (a.percentage || 0) - (b.percentage || 0));
    this.markRecommendedRows(processedData);
    return processedData;
  }

  private handleSubfreezingDryerWarnings(modelData: SuggestedModel): void {
    if (this.fetchSensorValues?.sensorValues && this.MIN_INLET_TEMPERATURE in this.fetchSensorValues.sensorValues) {
      const minInlet = this.fetchSensorValues.sensorValues.min_inlet_temperature;

      if (minInlet < this.MIN_INLET_TEMP_LOW && modelData.model.maxFlow !== this.MAX_FLOW) {
        if (!modelData.model.model.includes('Low inlet temp heater')) {
          modelData.isHeaterRequired = true;
          modelData.model.model += '+ Low inlet temp heater';
          modelData.warningMessage =
            'Compressed air with a low inlet temperature heater is required for temperatures below 20°C in Sub-Freezing Dryers.';
        }
      } else if (minInlet < this.MIN_INLET_TEMP_VERY_LOW && modelData.model.maxFlow === this.MAX_FLOW) {
        modelData.warningMessage = 'Minimum air inlet temperature is too low, at least +15°C required in SubFreezing Dryers.';
      } else if (minInlet < this.MIN_INLET_TEMP_LOW && modelData.model.maxFlow === this.MAX_FLOW) {
        modelData.warningMessage = 'Minimum air inlet temperature is too low, pressure dew point not guaranteed in SubFreezing Dryers';
      } else {
        modelData.warningMessage = '';
      }
    }
  }

  getDewPoint(sensors: SensorEntity[]): string | null | number {
    if (sensors) {
      const sensor = sensors.find((s) => s.name === 'dew_point');
      return sensor ? sensor.value : null;
    }
    return null;
  }

  updateMaxFlowUnitForRow(row): void {
    const maxFlow = row?.exactModel?.maxFlow || row?.maxFlow;
    if (maxFlow) {
      if (!row.convertedMaxFlow || row.convertedMaxFlow.unit !== this.selectedUnit) {
        this.unitConversionService.convert(maxFlow, 'm³/h', this.selectedUnit, 'airFlow').subscribe((convertedValue) => {
          if (row?.exactModel) {
            row.exactModel.maxFlow = convertedValue;
          } else {
            row.maxFlow = convertedValue;
          }

          row.convertedMaxFlow = { value: convertedValue, unit: this.selectedUnit };
          this.cdr.detectChanges();
        });
      } else {
        if (row?.exactModel) {
          row.exactModel.maxFlow = row.convertedMaxFlow.value;
        } else {
          row.maxFlow = row.convertedMaxFlow.value;
        }
      }
    }
  }

  toggleRow(row: SelectedRow): void {
    if (this.selectedRows.length >= this.MAX_SELECTED_ROWS && !this.selectedRows.includes(row)) {
      return;
    }

    const index = this.selectedRows.findIndex((r) => r === row);
    if (index === -1) {
      this.addSelectedRow(row);
    } else {
      this.removeSelectedRow(row, index);
    }

    this.productservice.setSelectedCheckbox([...this.selectedRows]);
  }

  private addSelectedRow(row: SelectedRow): void {
    if (this.selectedRows.length < this.MAX_SELECTED_ROWS) {
      row.selected = true;
      this.selectedRows.push(row);
      this.pushRowWithoutModelString(row);
    }
  }

  private removeSelectedRow(row: SelectedRow, index: number): void {
    row.selected = false;
    this.selectedRows.splice(index, 1);
    this.removeFromFilteredRows(row);
    this.productservice.setFilteredSelectedRows(this.filteredSelectedRows);
  }

  private removeFromFilteredRows(row: SelectedRow): void {
    let cleanModel: string | undefined;
    let filteredIndex = -1;

    if (row.exactModel && row.exactModel.model) {
      cleanModel = row.exactModel.model.split('+')[0].trim();
      filteredIndex = this.filteredSelectedRows.findIndex((r) => r.exactModel?.model.startsWith(cleanModel));
    } else if (row.model) {
      cleanModel = row.model.split('+')[0].trim();
      filteredIndex = this.filteredSelectedRows.findIndex((r) => r.model?.startsWith(cleanModel));
    }

    if (filteredIndex !== -1) {
      this.filteredSelectedRows.splice(filteredIndex, 1);
    }
  }

  onCheckboxChange(event: MatCheckboxChange, row: SelectedRow): void {
    if (this.selectedRows.length >= this.MAX_SELECTED_ROWS && !row.selected) {
      const alertMessage = this.translate.instant('ALERT.MAX_SELECTION');
      alert(alertMessage);
      event.source.checked = false;
      return;
    }
    this.toggleRow(row);
  }

  toggleAllRows(event: Event): void {
    const checked = event?.['checked'];
    this.dataSource.data.forEach((row) => (row.selected = checked));
  }

  isSelected(row: SelectedRow): boolean {
    return row.selected;
  }

  isAllSelected(): boolean {
    return this.dataSource.data.every((row) => row?.selected);
  }

  isAnySelected(): boolean {
    return this.dataSource.data.some((row) => row?.selected);
  }

  toggleRowDetail(row: any): void {
    row.expanded = !row.expanded;
    if (row.expanded) {
      this.computeRowSideEffects(row);
      this.updateMaxFlowUnitForRow(row);

      if (!row.imageSource) {
        row.isImageLoading = true;

        const modelName = row.model || row?.exactModel?.model;
        this.fetchImageDisplaySourceByDryerGroup(modelName).subscribe(
          (imageSource) => {
            row.imageSource = imageSource;
            row.isImageLoading = false;
            this.cdr.detectChanges();
          },
          (_error) => {
            row.isImageLoading = false;
            this.cdr.detectChanges();
          },
        );
      }
    }
  }

  checkForAnyLimitSuccess(): boolean {
    if (this.allDataItems.length === 0) {
      return false;
    }

    const anyModelExists = this.allDataItems.some((item) => item.exactModel !== null);

    if (!anyModelExists) {
      this.updateAccordionsBasedOnLimits();
      return false;
    }

    return true;
  }

  private updateAccordionsBasedOnLimits(): void {
    this.resetAccordions();
    this.expandAccordionsForOutOfRangeItems();
  }

  private resetAccordions(): void {
    for (const key in this.expandedAccordions) {
      this.expandedAccordions[key as keyof typeof this.expandedAccordions] = false;
    }
  }

  private expandAccordionsForOutOfRangeItems(): void {
    this.allDataItems.forEach((item) => {
      if (!item.withinRange) {
        this.expandAccordionForDryer(item.dryerGroup, item.dryerCategory, item.dryerType);
      }
    });
  }

  private expandAccordionForDryer(dryerGroup: string, dryerCategory: string, dryerType: string): void {
    const group = (dryerGroup || '').toLowerCase().trim();
    const category = (dryerCategory || '').toLowerCase().trim();
    const type = (dryerType || '').toLowerCase().trim();

    if (group.includes(DryerFilterTableComponent.DRYER_GROUPS.REFRIGERATED)) {
      this.handleRefrigeratedDryers(category, type);
    } else if (this.isSubfreezingDryer(group)) {
      this.expandedAccordions.subfreezingAirCooled = true;
    } else if (group.includes(DryerFilterTableComponent.DRYER_GROUPS.DESICCANT)) {
      this.handleDesiccantDryers(category, type);
    } else if (group.includes(DryerFilterTableComponent.DRYER_GROUPS.VSD)) {
      this.handleVsdDryers(type);
    }
  }

  private isSubfreezingDryer(group: string): boolean {
    return (
      group.includes(DryerFilterTableComponent.DRYER_GROUPS.SUBFREEZING) ||
      group.includes(DryerFilterTableComponent.DRYER_GROUPS.SUB_FREEZING)
    );
  }

  private handleRefrigeratedDryers(category: string, type: string): void {
    const conditions = [
      {
        match: () =>
          category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.CYCLING) &&
          !category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.NON_CYCLING) &&
          type.includes(DryerFilterTableComponent.DRYER_TYPES.AIR),
        accordion: 'refrigeratedCycling',
      },
      {
        match: () =>
          category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.NON_CYCLING) &&
          category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.CYCLING) &&
          type.includes(DryerFilterTableComponent.DRYER_TYPES.AIR),
        accordion: 'refrigeratedNonCycling',
      },
      {
        match: () =>
          category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.NON_CYCLING) &&
          category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.CYCLING) &&
          type.includes(DryerFilterTableComponent.DRYER_TYPES.WATER),
        accordion: 'refrigeratedWaterCooled',
      },
      {
        match: () => this.isHighTemperatureCategory(category) && type.includes(DryerFilterTableComponent.DRYER_TYPES.AIR),
        accordion: 'refrigeratedHighTemp',
      },
    ];

    const matchingCondition = conditions.find((condition) => condition.match());
    if (matchingCondition) {
      this.expandedAccordions[matchingCondition.accordion] = true;
    }
  }

  private isHighTemperatureCategory(category: string): boolean {
    return DryerFilterTableComponent.HIGH_TEMP_KEYWORDS.some((keyword) => category.includes(keyword));
  }

  private handleDesiccantDryers(category: string, type: string): void {
    if (this.isHeatedType(type)) {
      this.expandedAccordions.desiccantHeated = true;
    } else if (type.includes(DryerFilterTableComponent.DRYER_TYPES.HEATLESS)) {
      this.handleHeatlessDesiccantDryers(category);
    }
  }

  private isHeatedType(type: string): boolean {
    return (
      type.includes(DryerFilterTableComponent.DRYER_TYPES.HEATED) ||
      type === DryerFilterTableComponent.DRYER_TYPES.HEAT ||
      type.includes(`${DryerFilterTableComponent.DRYER_TYPES.HEAT} `) ||
      type.includes(` ${DryerFilterTableComponent.DRYER_TYPES.HEAT}`)
    );
  }

  private handleVsdDryers(type: string): void {
    if (type.includes(DryerFilterTableComponent.DRYER_TYPES.WATER)) {
      this.expandedAccordions.vsdWaterCooled = true;
    } else {
      this.expandedAccordions.vsdAirCooled = true;
    }
  }

  private handleHeatlessDesiccantDryers(category: string): void {
    if (!category) {
      return;
    }

    if (
      category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.TWIN) ||
      category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.TOWER)
    ) {
      this.expandedAccordions.desiccantTwinTower = true;
    } else if (category.includes(DryerFilterTableComponent.DRYER_CATEGORIES.MODULAR)) {
      this.expandedAccordions.desiccantModular = true;
    }
  }

  fetchImageDisplaySourceByDryerGroup(modelName: string): Observable<string> {
    return this.productservice.getActualImagePathUsingModelName(modelName).pipe(
      map((imageData: string) => {
        return imageData ? `data:image/jpeg;base64,${imageData}` : null;
      }),
    );
  }

  private processData(data: any[]): any[] {
    const processedData: any[] = [];
    this.allDataItems = JSON.parse(JSON.stringify(data));

    if (data.length === 0) {
      this.showNoModelsMessage = false;
      this.resetAllAccordions();
      return processedData;
    }

    if (this.tab === this.TAB_MODEL) {
      this.processModelTabData(data, processedData);
    } else {
      this.processConditionTabData(data, processedData);
    }

    processedData.sort((a, b) => (a.percentage || 0) - (b.percentage || 0));
    this.markRecommendedRows(processedData);

    // Show message only if there was actual data sent but no valid models found
    this.showNoModelsMessage = data.length > 0 && processedData.length === 0;

    this.updateAccordionExpansion();

    return processedData;
  }

  private processModelTabData(data: any[], processedData: any[]): void {
    data.forEach((item) => {
      if (item.exactModel !== null) {
        const modelDetails = {
          exactModel: item.exactModel,
          pressureDrop: item.pressureDrop,
          calculatedId: item.calculatedId,
          percentage: item.matchPercentage,
          outletAirFlow: item.outletAirFlow,
        };
        processedData.push(modelDetails);
        this.modelAvailable = true;
      } else {
        this.modelAvailable = false;
      }
    });
  }

  private processConditionTabData(data: any[], processedData: any[]): void {
    // Note: allDataItems is already populated in processData

    for (const dataArray of data) {
      const modelDetails = this.createModelDetails(dataArray);

      if (modelDetails.exactModel !== null) {
        this.modelAvailable = true;
        processedData.push(modelDetails);
        this.addSuggestedModelsToProcessedData(modelDetails, processedData);
      } else {
        this.modelAvailable = false;
      }
    }
  }

  private createModelDetails(item: any): any {
    return {
      exactModel: item?.exactModel ? item.exactModel : null,
      pressureDrop: item?.pressureDrop ? item.pressureDrop : null,
      calculatedId: item?.calculatedId !== null && item?.calculatedId !== undefined ? item?.calculatedId : null,
      percentage: item?.airFlowMargin && item?.airFlowMargin !== 0 && item?.airFlowMargin !== undefined ? item?.airFlowMargin : null,
      purgeFlow: item?.purgeFlow && item?.purgeFlow !== 0.0 && item?.purgeFlow !== undefined ? item?.purgeFlow : 0.0,
      outletAirFlow: item?.outletAirFlow && item?.outletAirFlow !== 0.0 && item?.outletAirFlow !== undefined ? item?.outletAirFlow : 0.0,
    };
  }

  private addSuggestedModelsToProcessedData(modelDetails: any, processedData: any[]): void {
    const suggestedModels = modelDetails?.exactModel?.suggestedModels;
    if (suggestedModels && suggestedModels.length > 0) {
      suggestedModels.forEach((suggestedModel: any) => {
        if (suggestedModel) {
          // Tag with the parent exact model's id so the suggestion stays in its category group.
          processedData.push({ ...suggestedModel.model, categoryGroupId: modelDetails.calculatedId });
        }
      });
    }
  }

  private resetAllAccordions(): void {
    Object.keys(this.DRYER_ACCORDION_CONFIG).forEach((key) => {
      this.expandedAccordions[key] = false;
    });
  }

  hasAnyItemsWithinRange(): boolean {
    return this.allDataItems.some((item) => item.withinRange === true);
  }

  // Check if all dryers are within range but no exact models are available
  public hasAllInRangeButNoModels(): boolean {
    return (
      this.allDataItems.length > 0 &&
      this.allDataItems.every((item) => item.withinRange === true) &&
      this.allDataItems.every((item) => item.exactModel === null)
    );
  }

  // Check if some dryers are in range but no exact models are available
  hasSomeInRangeButNoModels(): boolean {
    return this.hasAnyItemsWithinRange() && this.allDataItems.every((item) => item.exactModel === null);
  }

  // Check if some dryers are out of range
  hasSomeOutOfRange(): boolean {
    return this.allDataItems.some((item) => item.withinRange === false);
  }

  getInRangeDryerTypes(): string[] {
    return this.allDataItems
      .filter((item) => item.withinRange === true)
      .map((item) => `${item.dryerType} ${item.dryerCategory}`)
      .filter((value, index, self) => self.indexOf(value) === index);
  }
}
